import React from 'react';
import styled from 'styled-components';
import {
  Building2,
  Scale,
  Users,
  Gavel,
  ChevronRight,
  Handshake
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const IntroCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
  text-align: center;

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.75rem;
    color: var(--primary-blue);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;

    svg {
      color: var(--accent-gold);
    }
  }

  p {
    font-size: 1.1rem;
    color: var(--text-medium);
    line-height: 1.8;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  overflow: hidden;
  transition: var(--transition);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }
`;

const PartnerHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 48px;
    height: 48px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    padding: 0.75rem;
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    font-weight: 700;
  }
`;

const PartnerList = styled.ul`
  padding: 1.5rem;
  list-style: none;
`;

const PartnerItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--border-light);
  color: var(--text-dark);
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &:hover {
    color: var(--primary-blue);
  }
`;

const ICON_MAP = {
  Building2,
  Scale,
  Users,
  Gavel
};

const Partenaires = () => {
  const { content } = useSiteContent('partenaires_page');
  const data = content || {};
  const groups = Array.isArray(data.groups) ? data.groups : [];

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>{data.heroTitle || 'Institutions Partenaires'}</HeroTitle>
          <HeroSubtitle>
            {data.heroSubtitle || "La Médiature de la République collabore avec un réseau diversifié d'institutions pour renforcer la protection des citoyens et promouvoir la bonne gouvernance"}
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentWrapper>
          <IntroCard>
            <h2><Handshake size={28} /> {data.introTitle || 'Notre réseau de partenaires'}</h2>
            <p>
              {data.introText || "Les partenariats stratégiques de la Médiature permettent d'amplifier son impact et d'assurer une couverture nationale efficace. Ensemble, nous œuvrons pour renforcer la confiance entre les citoyens et l'administration publique."}
            </p>
          </IntroCard>

          <PartnersGrid>
            {groups.map((group, index) => {
              const Icon = ICON_MAP[group.icon] || Building2;
              const items = Array.isArray(group.items) ? group.items : [];

              return (
                <PartnerCard key={`${group.title || 'group'}-${index}`}>
                  <PartnerHeader>
                    <Icon size={24} />
                    <h3>{group.title || ''}</h3>
                  </PartnerHeader>
                  <PartnerList>
                    {items.map((item, idx) => (
                      <PartnerItem key={`item-${idx}`}>
                        <ChevronRight size={18} />
                        {item}
                      </PartnerItem>
                    ))}
                  </PartnerList>
                </PartnerCard>
              );
            })}
          </PartnersGrid>
        </ContentWrapper>
      </Container>
    </PageContainer>
  );
};

export default Partenaires;
